import React from 'react';
import { useTranslation } from 'react-i18next';
import { ToastContext, Modal, SplitContainer } from '@weezevent/nacre';
import Chart from '../../../../../models/chart';
import ZoneModel from '../../../../../models/zoneModel';
import ZoneEditorContainer from './zoneEditorContainer';
import ZonesRightComponent from './zoneRightComponent';
import Style from './zoneEditorModal.module.css';

const MAP_TYPE_STYLE = {
    schematic: {
        opacity: 0,
        hover_opacity: 0,
        unavailable_opacity: 0.8,
        unavailable_color: 'white',
        unavailable_hover_opacity: 0.8,
        unavailable_hover_color: 'white',
        stroke_width: 0,
        unavailable_stroke_width: 0,
        hover_stroke_width: 0,
        unavailable_hover_stroke_width: 0
    },
    realistic: {
        opacity: 0.4,
        hover_opacity: 0.7,
        unavailable_opacity: 0,
        unavailable_color: 'white',
        unavailable_hover_opacity: 0,
        unavailable_hover_color: 'white',
        stroke_width: 0,
        unavailable_stroke_width: 0,
        hover_stroke_width: 0,
        unavailable_hover_stroke_width: 0
    }
};

export default function zoneEditorModal({ organization, chart, zone, zones, open, onClose }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const [loading, setLoading] = React.useState(false);
    const [currentZone, setCurrentZone] = React.useState(null);
    const [parent, setParent] = React.useState(null);
    const [updateNonChildren, setUpdateNonChildren] = React.useState(false);
    const [disableSave, setDisable] = React.useState(true);

    const handlePoints = React.useCallback(
        points => {
            if (!currentZone) {
                return;
            }
            let child = parent.children.find(c => c.uuid === currentZone.uuid);
            child.seatlib_data.points = child.seatlib_data.points ? [...child.seatlib_data.points, points] : [points];
            child.__availability = child.seatlib_data.points.length;
            setDisable(false);
            setCurrentZone({ ...child });
        },
        [currentZone, parent]
    );

    const handleStyle = React.useCallback(
        style => {
            if (!currentZone) {
                return;
            }
            let child = parent.children.find(c => c.uuid === currentZone.uuid);
            child.seatlib_data = { ...child.seatlib_data, ...style };
            setDisable(false);
            setCurrentZone({ ...child });
        },
        [currentZone, parent]
    );

    const clear = React.useCallback(
        (zoneId = null) => {
            let children = [...parent.children];
            children.forEach(child => {
                if (zoneId === null || child.uuid === zoneId) {
                    child.seatlib_data.points = [];
                    child.__availability = 0;
                }
            });
            setDisable(false);
            setParent({ ...parent, children: [...children] });
        },
        [parent]
    );

    const saveZones = React.useCallback(
        parent => {
            const { uuid, seatlib_data } = parent;
            const parentModel = zone ? new ZoneModel({ uuid, seatlib_data }) : new Chart({ uuid, seatlib_data });

            let non_children_zones = [];
            // If we update the map type in chart
            if (updateNonChildren) {
                // find every zone that are not part of the children
                // and update map type style
                non_children_zones = zones.filter(zone => !parent.children.map(({ uuid }) => uuid).includes(zone.uuid));
                non_children_zones.forEach(zone => (zone.seatlib_data = { ...zone.seatlib_data, ...MAP_TYPE_STYLE[seatlib_data.map_type] }));
            }
            setLoading(true);
            return Promise.all([
                parentModel.save({
                    organizationId: organization.pk,
                    chartId: chart.pk
                }),
                ZoneModel.save(
                    [...non_children_zones, ...parent.children].map(({ uuid, seatlib_data }) => {
                        return new ZoneModel({ uuid, seatlib_data: { ...seatlib_data, map_type: parent.seatlib_data.map_type } });
                    }),
                    { organizationId: organization.pk, chartId: chart.pk },
                    { includes_deleted_zones: true }
                )
            ])
                .then(() => {
                    toast.success(t('ticket.charts.zone_editor.save_success'));
                    setLoading(false);
                    setUpdateNonChildren(false);
                    setDisable(true);
                    onCloseEditChart(false);
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.charts.zone_editor.save_error'));
                });
        },
        [organization, chart, toast, t, zone, zones, updateNonChildren]
    );

    const handleSubmit = React.useCallback(() => {
        saveZones(parent);
        setCurrentZone(null);
    }, [saveZones, parent]);

    const handleSelectedZone = React.useCallback(
        zone => {
            if (!currentZone) {
                return setCurrentZone(zone);
            }
            if (zone.uuid === currentZone.uuid) {
                return setCurrentZone(null);
            }
            return setCurrentZone(zone);
        },
        [currentZone]
    );

    const handleSwitchType = React.useCallback(
        checked => {
            const tmpChildren = [...parent.children];
            setUpdateNonChildren(!updateNonChildren);
            setDisable(false);
            tmpChildren.map(child => {
                child.seatlib_data = { ...child.seatlib_data, ...MAP_TYPE_STYLE[checked ? 'realistic' : 'schematic'] };
                return child;
            });
            if (currentZone) {
                setCurrentZone({ ...tmpChildren.find(c => c.uuid === currentZone.uuid) });
            }
            return setParent({ ...parent, children: tmpChildren, seatlib_data: { ...parent.seatlib_data, map_type: checked ? 'realistic' : 'schematic' } });
        },
        [parent, currentZone, updateNonChildren]
    );

    React.useEffect(() => {
        if (!zones) {
            return;
        }
        const children = zones.filter(child => child.parent === (zone?.pk || null));
        const parent = {
            uuid: zone?.pk || chart?.pk,
            children: children.map(child => {
                child.categories = [];
                child.available = true;
                // To check if we need to init the child with a style
                // only check if child map type is different from chart
                // because only in chart we can switch type
                if (chart?.seatlib_data.map_type !== child.seatlib_data.map_type || !chart?.seatlib_data.map_type) {
                    child.seatlib_data = { ...child.seatlib_data, ...MAP_TYPE_STYLE[chart?.seatlib_data.map_type || 'schematic'] };
                }
                // we use __availability property to display number of blocs in zone selector
                child.__availability = child.seatlib_data.points?.length || 0;
                return child.toAPI();
            }),
            name: zone?.name || 'root',
            preview_picture: zone ? zone.preview_picture : chart?.preview_picture,
            seatlib_data: zone ? { ...zone.seatlib_data } : { ...chart?.seatlib_data },
            categories: []
        };
        // For now we do not want to switch map type in Zone, only in Chart
        // So zone map_type must be iso to chart
        // For later compatibility when we will allow zone and chart to have different type
        // we init a value in the parent
        parent.seatlib_data.map_type = chart?.seatlib_data.map_type || 'schematic';

        setParent(parent);
    }, [zones, chart]);

    React.useEffect(() => {
        if (!parent) {
            return;
        }
        let children = [...parent.children];
        children.forEach(child => {
            child.available = Boolean(currentZone === null || child.uuid === currentZone.uuid);
        });
        setParent({ ...parent, children: [...children] });
    }, [currentZone]);

    const onCloseEditChart = React.useCallback(() => {
        onClose(false);
        setCurrentZone(null);
    }, [onClose]);

    if (!parent) {
        return null;
    }

    return (
        <>
            {open && (
                <Modal size="fullscreen" className={Style['chart-edit-modal']} open={open} onClose={onCloseEditChart} allowExternalClosing={false}>
                    <SplitContainer
                        className={Style['split-container']}
                        additional={
                            <ZonesRightComponent
                                parent={parent}
                                handleSubmit={handleSubmit}
                                handleSelectedZone={handleSelectedZone}
                                disableSave={disableSave}
                                clear={clear}
                                onCloseEditChart={onCloseEditChart}
                                loading={loading}
                            />
                        }
                    >
                        <ZoneEditorContainer
                            zone={zone}
                            parent={parent}
                            currentZone={currentZone}
                            handlePoints={handlePoints}
                            handleStyle={handleStyle}
                            clear={clear}
                            handleSwitchType={handleSwitchType}
                        />
                    </SplitContainer>
                </Modal>
            )}
        </>
    );
}
